import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ApiService } from '../../../core/api-service';
import { FeedbackService } from '../../../core/feedback.service';
import { EMPTY, mergeMap, of, Subject, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectAccount, selectAccountID, selectAiRepliesActivated } from '../../../store/global/core-selectors';
import { catchError, filter, map, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { selectAiAssistedReplies, selectAiLanguageCode, selectAiLanguageForReviewType, selectAiReplyStyle, selectInvalidListingsFilterSelection, selectPaginatorData, selectReviewFilters, selectReviewRequestParams, selectReviewTranslations, selectSelectedAiClosing, selectSelectedAiGreeting } from './review.selectors';
import { cloneDeep } from 'lodash';
import { getNewPageIndex, sendToGoogleAnalytics } from '../../../core/utilities';
import { ReplyTemplateService } from '../reply-template.service';
import { ReplyTemplate } from '../reply-tab-group/template-selection/template-selection.component';
import { ReviewRequestParams } from '../../../models/review-request-params';
import { ReviewTranslation } from '../../../models/review-translation';
import { saveAs } from 'file-saver';
import { formatDate, parseISODateString } from '../../../core/utils/date-fns-utils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AiReplyActions, AiSettingsActions, cancelExport, ExportReplyTemplatesActions, ExportReviewsActions, handleBeError, ReplyTemplatesActions, ReviewActions, reviewComponentDestroyed, ReviewsActions, ReviewSyncActions } from './review-actions';
import { ReviewExportFormatChoice } from '../review-export-popup/review-export-popup.component';
import { ReplyStyle } from '../reply-tab-group/ai-replies-settings/ai-replies-settings.component';
import { AiAssistedReply } from '../../../models/ai-assisted-reply';
import { Router } from '@angular/router';
import { updateSelectedAccount } from '../../../store/global/core-actions';
import { SortType } from '../../../models/campaigns-models/campaign-filters';
import { endOfDay, startOfDay, subDays } from 'date-fns';
import { selectCurrentRoute } from '../../../store/global/router-selectors';
import { MainRoutes } from '../../../core/enums';
import { StoreActionsDispatchService } from '../../../core/store-actions-dispatch.service';
import { AnsweringAction } from '../answering-action';


@Injectable()
export class ReviewEffects {
  constructor(private actions$: Actions,
              private apiService: ApiService,
              private feedbackService: FeedbackService,
              private store: Store,
              private translate: TranslateService,
              private replyTemplateService: ReplyTemplateService,
              private snackBar: MatSnackBar,
              private router: Router,
              private storeActionDispatcher: StoreActionsDispatchService) {
  }

  getReviews$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(
            ReviewsActions.getReviews,
            reviewComponentDestroyed,
            ReviewSyncActions.syncReviewFilters,
            ReviewSyncActions.syncReviewPaginatorData),
        // get params
        concatLatestFrom(() => [this.store.select(selectAccountID),
          this.store.select(selectReviewRequestParams)]),
        switchMap(([action, accountId, reviewRequestParams]) => {
              const _reviewRequestParams = cloneDeep(reviewRequestParams);

              // set requestedPage = 1 (pageIndex = 0) if action was syncReviewFilters
              // could result in BE error otherwise
              if (action.type === ReviewSyncActions.syncReviewFilters.type) {
                _reviewRequestParams.requestedPage = 1;
              }

              return action.type === reviewComponentDestroyed.type ||
              action.type === ReviewSyncActions.syncReviewPaginatorData.type && action.preventReviewUpdate ||
              action.type === ReviewSyncActions.syncReviewFilters.type && action.preventReviewUpdate ||
              reviewRequestParams.gbpLocationIds === null ? // no intersection = cancel any ongoing requests
                  EMPTY :
                  this.apiService.getReviews(accountId, _reviewRequestParams)
                      .pipe(
                          mergeMap((res) => {
                            const returnActions: any[] = [ReviewsActions.getReviewsSuccess(
                                { reviewsResponse: res?.result }),
                            ];

                            // reset pageIndex if action was syncReviewFilters
                            if (action.type === ReviewSyncActions.syncReviewFilters.type) {
                              returnActions.push(ReviewSyncActions.syncReviewPaginatorData({
                                pageIndex: 0,
                                pageSize: _reviewRequestParams.pageSize,
                                preventReviewUpdate: true,
                              }));
                            }

                            return returnActions;
                          }),
                          catchError((err: HttpErrorResponse) => {
                            const errorPayload = {
                              userFriendlyMessage: this.translate.instant('REVIEWS.LOADING_REVIEWS_FAILED'),
                              error: err,
                            };

                            // retrying makes sense if it was caused by reaching the memory limit
                            if (err.status === 500) {
                              const button = {
                                text: this.translate.instant('REVIEWS.DOWNLOAD.RETRY'),
                                action: action.type === ReviewsActions.getReviews.type ?
                                    () => this.storeActionDispatcher.dispatch(ReviewsActions.getReviews) :
                                    action.type === ReviewSyncActions.syncReviewFilters.type ?
                                        () => this.storeActionDispatcher.dispatch(ReviewSyncActions.syncReviewFilters, {
                                          reviewFilters: action.reviewFilters,
                                          preventReviewUpdate: action.preventReviewUpdate,
                                        }) :
                                        action.type === ReviewSyncActions.syncReviewPaginatorData.type ?
                                            () => this.storeActionDispatcher.dispatch(ReviewSyncActions.syncReviewPaginatorData, {
                                              pageSize: action.pageSize,
                                              pageIndex: action.pageIndex,
                                              totalNumber: action.totalNumber,
                                              preventReviewUpdate: action.preventReviewUpdate,
                                            }) :
                                            null,
                              };

                              errorPayload['actionButtons'] = [button];
                            }

                            return of(handleBeError(errorPayload));
                          }),
                      );
            },
        ),
    );
  });

  translateReview$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(ReviewActions.translateReview),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        mergeMap(([action, accountId]) => {
          return this.apiService.translateReview(accountId, action.reviewId, action.languageCode)
              .pipe(
                  map((res) => {
                    const translation: ReviewTranslation = {
                      id: `${action.reviewId}_${action.languageCode}`,
                      reviewId: action.reviewId,
                      languageCode: action.languageCode,
                      reviewTranslatedText: res.result.reviewTranslatedText,
                    };
                    return ReviewActions.translateReviewSuccess({ reviewTranslation: translation });
                  }),
                  catchError((err: HttpErrorResponse) => of(handleBeError({
                    userFriendlyMessage: this.translate.instant('REVIEWS.TRANSLATION_FAILED'),
                    error: err,
                  })).pipe(
                      mergeMap((a) => [a, ReviewActions.translateReviewError({ reviewId: action.reviewId })]),
                  )),
              );
        }),
    );
  });

  resetReviewsOnInvalidListingsFilterSelection$ = createEffect(() => {
    return this.store.select(selectInvalidListingsFilterSelection).pipe(
        filter((invalid) => invalid === true),
        concatLatestFrom(() => [this.store.select(selectPaginatorData),
          this.store.select(selectReviewFilters)]),
        mergeMap(([_, paginatorData, filters]) => [
          ReviewsActions.getReviewsSuccess({
            reviewsResponse: {
              from: filters.from,
              to: filters.to,
              reviews: [],
              totalNumber: 0,
            },
          }),
          ReviewSyncActions.syncReviewPaginatorData({
            pageIndex: 0,
            pageSize: paginatorData.pageSize,
            preventReviewUpdate: true,
          })],
        ));
  });

  syncUrlWithFilters$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(ReviewSyncActions.syncReviewFilters),
        tap((action) => {
          this.router.navigate([], {
            queryParams: {
              ...action.reviewFilters,
              // query date params should b in format yyyy-MM-dd
              from: action.reviewFilters.from ? formatDate(new Date(action.reviewFilters.from)) : null,
              to: action.reviewFilters.to ? formatDate(new Date(action.reviewFilters.to)) : null,
              // limit to 10 items per listings category
              cities: action.reviewFilters.cities.length > 10 ?
                  action.reviewFilters.cities.slice(0, 10) :
                  action.reviewFilters.cities,
              countryCodes: action.reviewFilters.countryCodes.length > 10 ?
                  action.reviewFilters.countryCodes.slice(0, 10) :
                  action.reviewFilters.countryCodes,
              storeCodes: action.reviewFilters.storeCodes.length > 10 ?
                  action.reviewFilters.storeCodes.slice(0, 10) :
                  action.reviewFilters.storeCodes,
              businessNames: action.reviewFilters.businessNames.length > 10 ?
                  action.reviewFilters.businessNames.slice(0, 10) :
                  action.reviewFilters.businessNames,
              gbpAccountIds: action.reviewFilters.gbpAccountIds.length > 10 ?
                  action.reviewFilters.gbpAccountIds.slice(0, 10) :
                  action.reviewFilters.gbpAccountIds,
            },
            replaceUrl: true, // replace current state in navigation history
          });
        }),
    );
  }, { dispatch: false });

  saveReviewReply$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(ReviewActions.answerReviewReply, reviewComponentDestroyed),
        concatLatestFrom((action) => [
          this.store.select(selectAccountID),
          this.store.select(selectPaginatorData),
        ]),
        switchMap(([action, accountId, paginatorData]) => action.type === reviewComponentDestroyed.type ?
            EMPTY : this.apiService.answerReviewReply(action.id, action.gbpLocationId, action.replyText, accountId, action.currentAnsweringAction === AnsweringAction.DELETING)
                .pipe(
                    map((res) => {

                      if (action.currentAnsweringAction === AnsweringAction.REPLYING) {
                        sendToGoogleAnalytics('Review activity', 'reply', 'Reply to review');
                      } else if (action.currentAnsweringAction === AnsweringAction.EDITING) {
                        sendToGoogleAnalytics('Review activity', 'edit', 'Edit reply of a review');
                      } else if (action.currentAnsweringAction === AnsweringAction.DELETING) {
                        sendToGoogleAnalytics('Review activity', 'delete', 'Delete review reply');
                      }

                      return ReviewActions.saveReviewSuccess({ review: res.result });
                    }),
                    catchError((err: HttpErrorResponse) => of(EMPTY).pipe(mergeMap(() => {
                      const returnActions = [
                        handleBeError({
                          userFriendlyMessage: this.translate.instant(err?.status === 404 ?
                              'REVIEWS.REVIEW_WAS_DELETED' :
                              action.currentAnsweringAction === AnsweringAction.DELETING ?
                                  'REVIEWS.REPLY_DELETE_ERROR' :
                                  'REVIEWS.REPLY_ERROR'),
                          error: err,
                        }),
                      ] as any[];

                      if (err?.status === 404) {
                        const newTotal = paginatorData.totalNumber - 1;
                        returnActions.push(ReviewsActions.removeReview({ id: action.id }));
                        returnActions.push(ReviewSyncActions.syncReviewPaginatorData({
                          totalNumber: newTotal,
                          pageSize: paginatorData.pageSize,
                          pageIndex: getNewPageIndex(newTotal, paginatorData.pageSize, paginatorData.pageIndex),
                        }));
                      }

                      return returnActions;
                    }))),
                ),
        ),
    );
  });

  reportReview$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(ReviewActions.reportReview, reviewComponentDestroyed),
        concatLatestFrom(() => [
          this.store.select(selectAccountID),
          this.store.select(selectPaginatorData),
        ]),
        switchMap(([action, accountID, paginatorData]) => action.type === reviewComponentDestroyed.type ?
            EMPTY :
            this.apiService.reportReview(action.review.id, accountID)
                .pipe(
                    map((res) => {
                      this.feedbackService.showSuccessSnackbar({
                        data: {
                          userFriendlyMessage: this.translate.instant('REVIEWS.REPORT_SUCCESS'),
                        },
                      });

                      sendToGoogleAnalytics('Review activity', 'report', 'Report a review');

                      return ReviewActions.saveReviewSuccess({ review: res.result });
                    }),
                    catchError((err: HttpErrorResponse) => of(EMPTY).pipe(mergeMap(() => {
                      const returnActions = [
                        handleBeError({
                          userFriendlyMessage: this.translate.instant(err?.status === 404 ?
                              'REVIEWS.REVIEW_WAS_DELETED' :
                              'REVIEWS.REPORT_ERROR'),
                          error: err,
                        }),
                      ] as any[];

                      if (err?.status === 404) {
                        const newTotal = paginatorData.totalNumber - 1;
                        returnActions.push(ReviewsActions.removeReview({ id: action.review.id }));
                        returnActions.push(ReviewSyncActions.syncReviewPaginatorData({
                          totalNumber: newTotal,
                          pageSize: paginatorData.pageSize,
                          pageIndex: getNewPageIndex(newTotal, paginatorData.pageSize, paginatorData.pageIndex),
                        }));
                      }

                      return returnActions;
                    }))),
                ),
        ),
    );
  });

  getReplyTemplates$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(ReplyTemplatesActions.getReplyTemplates, reviewComponentDestroyed),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) => action.type === reviewComponentDestroyed.type ?
            EMPTY :
            this.apiService.getReplyTemplates(accountId)
                .pipe(
                    map((res) => {
                      if (res?.result) {
                        res.result.forEach((replyTemplate: ReplyTemplate) => {
                          replyTemplate.templateObject = this.replyTemplateService.generateQuillDelta(replyTemplate.templateText);
                        });
                        return ReplyTemplatesActions.getReplyTemplatesSuccess({ replyTemplates: res.result });
                      } else {
                        return ReplyTemplatesActions.getReplyTemplatesSuccess({ replyTemplates: [] });
                      }
                    }),
                    catchError((err: HttpErrorResponse) => of(handleBeError({
                      userFriendlyMessage: this.translate.instant('ERROR'),
                      error: err,
                    }))),
                ),
        ),
    );
  });

  getAiSettings$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(AiSettingsActions.getAiSettings, reviewComponentDestroyed),
        concatLatestFrom(() => [this.store.select(selectAccountID), this.store.select(selectAiRepliesActivated)]),
        switchMap(([action, accountId, aiRepliesActive]) =>
            (action.type === reviewComponentDestroyed.type) || !aiRepliesActive ?
                EMPTY :
                this.apiService.getAiSettings(accountId)
                    .pipe(
                        map((res) => {
                          if (res?.result) {
                            return AiSettingsActions.getAiSettingsSuccess({ aiSettings: res.result });
                          } else {
                            return AiSettingsActions.getAiSettingsSuccess({ aiSettings: [] });
                          }
                        }),
                        catchError((err: HttpErrorResponse) => of(handleBeError({
                          userFriendlyMessage: this.translate.instant('ERROR'),
                          error: err,
                        }))),
                    ),
        ),
    );
  });

  getDefaultLanguages$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(AiSettingsActions.getDefaultLanguages, reviewComponentDestroyed),
        concatLatestFrom(() => [this.store.select(selectAccountID), this.store.select(selectAiRepliesActivated)]),
        switchMap(([action, accountId, aiRepliesActive]) =>
            (action.type === reviewComponentDestroyed.type) || !aiRepliesActive ?
                EMPTY :
                this.apiService.getDefaultLanguages(accountId)
                    .pipe(
                        map((res) => {
                          return AiSettingsActions.getDefaultLanguagesSuccess({ defaultLanguages: res?.result ? res.result : [] });
                        }),
                        catchError((err: HttpErrorResponse) => of(handleBeError({
                          userFriendlyMessage: this.translate.instant('ERROR'),
                          error: err,
                        }))),
                    ),
        ),
    );
  });

  updateDefaultLanguage$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(AiSettingsActions.updateDefaultLanguage, reviewComponentDestroyed),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) => action.type === reviewComponentDestroyed.type ?
            EMPTY :
            this.apiService.updateDefaultLanguage(accountId, action.defaultLanguage, action.reviewType)
                .pipe(
                    map((res) => {
                      return AiSettingsActions.updateDefaultLanguageSuccess({ aiSettings: res?.result });
                    }),
                    catchError((err: HttpErrorResponse) => of(handleBeError({
                      userFriendlyMessage: this.translate.instant('ERROR'),
                      error: err,
                    }))),
                ),
        ),
    );
  });

  updateReplyStyle$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(AiSettingsActions.updateReplyStyle, reviewComponentDestroyed),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) => action.type === reviewComponentDestroyed.type ?
            EMPTY :
            this.apiService.updateReplyStyle(accountId, (action.style as ReplyStyle))
                .pipe(
                    map((res) => {
                      return AiSettingsActions.updateReplyStyleSuccess({ aiSettings: res?.result });
                    }),
                    catchError((err: HttpErrorResponse) => of(handleBeError({
                      userFriendlyMessage: this.translate.instant('ERROR'),
                      error: err,
                    }))),
                ),
        ),
    );
  });

  saveAiSettings$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(AiSettingsActions.saveAiSettings),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) => {
          return this.apiService.saveAiSettings(accountId, action.aiSettings)
              .pipe(
                  map((res) => {
                    if (res?.result) {
                      return AiSettingsActions.saveAiSettingsSuccess({ aiSettings: res.result });
                    } else {
                      return AiSettingsActions.saveAiSettingsSuccess({ aiSettings: null });
                    }
                  }),
                  catchError((err: HttpErrorResponse) => of(handleBeError({
                    userFriendlyMessage: this.translate.instant('ERROR'),
                    error: err,
                  }))),
              );
        }),
    );
  });

  selectAiSettings$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(AiSettingsActions.selectAiSettings),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) => {

          let aiSettingsCopy = cloneDeep(action.aiSettings);
          aiSettingsCopy.selected = true;

          return this.apiService.saveAiSettings(accountId, aiSettingsCopy)
              .pipe(
                  map((res) => {
                    if (res?.result) {
                      this.feedbackService.showSuccessSnackbar({
                        data: {
                          userFriendlyMessage: `${aiSettingsCopy.type.charAt(0).toUpperCase()}${aiSettingsCopy.type.slice(1).toLowerCase()} selected successfully.`,
                          dismissButton: {
                            hide: true,
                          },
                        },
                      });
                      return AiSettingsActions.selectAiSettingsSuccess({ aiSettings: res.result });
                    } else {
                      return AiSettingsActions.selectAiSettingsSuccess({ aiSettings: null });
                    }
                  }),
                  catchError((err: HttpErrorResponse) => of(handleBeError({
                    userFriendlyMessage: this.translate.instant('ERROR'),
                    error: err,
                  }))),
              );
        }),
    );
  });

  deleteAiSettings$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(AiSettingsActions.deleteAiSettings),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) => {
          return this.apiService.deleteAiSettings(accountId, action.id)
              .pipe(
                  map((res) => {
                    if (res?.result) {
                      return AiSettingsActions.deleteAiSettingsSuccess({ id: res.result.id });
                    } else {
                      return AiSettingsActions.deleteAiSettingsError();
                    }
                  }),
                  catchError((err: HttpErrorResponse) => of(handleBeError({
                    userFriendlyMessage: this.translate.instant('ERROR'),
                    error: err,
                  }))),
              );
        }),
    );
  });

  unselectAiSettings$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(AiSettingsActions.unSelectAiSettings),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) => {

          let aiSettingsCopy = cloneDeep(action.aiSettings);
          aiSettingsCopy.selected = false;

          return this.apiService.saveAiSettings(accountId, aiSettingsCopy)
              .pipe(
                  map((res) => {
                    if (res?.result) {
                      this.feedbackService.showSuccessSnackbar({
                        data: {
                          userFriendlyMessage: `No custom ${aiSettingsCopy.type.charAt(0).toUpperCase()}${aiSettingsCopy.type.slice(1).toLowerCase()} selected.`,
                          dismissButton: {
                            hide: true,
                          },
                        },
                      });
                      return AiSettingsActions.unSelectAiSettingsSuccess({ aiSettings: res.result });
                    } else {
                      return AiSettingsActions.unSelectAiSettingsSuccess({ aiSettings: null });
                    }
                  }),
                  catchError((err: HttpErrorResponse) => of(handleBeError({
                    userFriendlyMessage: this.translate.instant('ERROR'),
                    error: err,
                  }))),
              );
        }),
    );
  });

  exportReviews$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(ExportReviewsActions.exportReviews, cancelExport),
        concatLatestFrom(() => [
          this.store.select(selectAccount),
          this.store.select(selectReviewRequestParams),
        ]),
        switchMap(([action, account, reviewRequestParams]) => {
          if (ExportReviewsActions.exportReviews.type === action.type) {
            const reqParams = action.filtered ? reviewRequestParams : null;

            return this.apiService.exportReviews(reqParams, account.id, action.format)
                .pipe(
                    map((res) => {
                      let blob = res;

                      if (action.format === 'csv') {
                        blob = new Blob([res], { type: 'text/csv' });
                        sendToGoogleAnalytics('Export', 'csv export', action.filtered
                            ? 'Export filtered reviews as CSV'
                            : 'Export all reviews as CSV');
                      } else {
                        sendToGoogleAnalytics('Export', 'xlsx export', action.filtered
                            ? 'Export filtered reviews as XLSX'
                            : 'Export all reviews as XLSX');
                      }
                      saveAs(
                          blob,
                          this.generateExportFileName(reqParams, action.filtered, account.accountName, action.format),
                      );
                      this.snackBar.dismiss();
                      return ExportReviewsActions.exportReviewsSuccess();
                    }),
                    catchError((err: HttpErrorResponse) => {
                      // server error in this context should always mean that too many reviews were selected
                      // implemented in BLS-6310
                      if (err.status >= 500 && err.status <= 599) {
                        this.feedbackService.showAlertSnackbar({
                          data: {
                            userFriendlyMessage: this.translate.instant('REVIEWS.TOO_MANY_SELECTED'),
                          },
                        }, err.status);
                        return of(ExportReviewsActions.exportReviewsFail());
                      }

                      // 404 in this context should always mean that there were no reviews to be exported
                      // implemented in BLS-6448
                      if (err.status === 404) {
                        this.feedbackService.showAlertSnackbar({
                          data: {
                            userFriendlyMessage: this.translate.instant('REVIEWS.NOTHING_TO_EXPORT'),
                          },
                        }, err.status);
                        return of(ExportReviewsActions.exportReviewsFail());
                      }

                      console.error({ err, scope: 'exportReplyTemplates' });
                      // alert buttons
                      const userChoice = new Subject<boolean>(); // true means 'Retry'
                      // show snackbar
                      this.feedbackService.showAlertSnackbarWithRetryFromEffect({
                        userFriendlyMessage: this.translate.instant('REVIEWS.DOWNLOAD.ERROR_OCCURRED') + '\n' +
                            this.translate.instant('REVIEWS.DOWNLOAD.RETRY_DOWNLOAD?'),
                        retryButtonText: this.translate.instant('REVIEWS.DOWNLOAD.RETRY'),
                        err,
                        userChoice,
                      });

                      return userChoice.pipe(switchMap((res) => res ?
                          of(ExportReviewsActions.exportReviews({
                            filtered: action.filtered,
                            format: action.format,
                          })) :
                          of(ExportReviewsActions.exportReviewsFail()),
                      ));
                    }));
          } else return EMPTY;
        }));
  });

  exportReplyTemplates$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(ExportReplyTemplatesActions.exportReplyTemplates, cancelExport),
        concatLatestFrom(() => this.store.select(selectAccount)),
        switchMap(([action, account]) => ExportReplyTemplatesActions.exportReplyTemplates.type === action.type ?
            this.apiService.exportReplyTemplates(account.id)
                .pipe(
                    map((res) => {
                      const blob = new Blob([res], { type: 'text/csv' });

                      const username = account.accountName;
                      const date = formatDate(new Date(), 'dd.MM.yyyy');
                      const fileName = `${username}_templates_${date}.csv`;

                      saveAs(blob, fileName);
                      return ExportReplyTemplatesActions.exportReplyTemplatesSuccess();
                    }),
                    catchError((err: HttpErrorResponse) => {
                      // alert buttons
                      const userChoice = new Subject<boolean>(); // true means 'Retry'
                      // show snackbar
                      this.feedbackService.showAlertSnackbarWithRetryFromEffect({
                        userFriendlyMessage: this.translate.instant('REVIEWS.TEMPLATES.ERROR_OCCURRED') + '\n' +
                            this.translate.instant('REVIEWS.TEMPLATES.RETRY_DOWNLOAD?'),
                        retryButtonText: this.translate.instant('REVIEWS.DOWNLOAD.RETRY'),
                        err,
                        userChoice,
                      });

                      console.error({ err, scope: 'exportReplyTemplates' });

                      return userChoice.pipe(switchMap((res) => res ?
                          of(ExportReplyTemplatesActions.exportReplyTemplates()) :
                          of(ExportReplyTemplatesActions.exportReplyTemplatesFail()),
                      ));
                    }),
                )
            : EMPTY,
        ),
    );
  });

  getAiAssistedReply$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(AiReplyActions.getAiAssistedReply, reviewComponentDestroyed),
        concatLatestFrom(() => [this.store.select(selectAccountID),
          this.store.select(selectSelectedAiGreeting),
          this.store.select(selectSelectedAiClosing),
          this.store.select(selectAiLanguageCode),
          this.store.select(selectAiLanguageForReviewType),
          this.store.select(selectAiReplyStyle),
        ]),
        switchMap(([action, accountId, selectedAiGreeting, selectedAiClosing, language, reviewType, replyStyle]) => action.type === reviewComponentDestroyed.type ?
            EMPTY :
            this.apiService.getAiAssistedReply(accountId, action.review?.id, selectedAiGreeting?.text, selectedAiClosing?.text, language, reviewType, replyStyle)
                .pipe(
                    map((res) => {
                      const aiReply: AiAssistedReply = res.result ?
                          {
                            ...res.result, reply: this.replyTemplateService
                                .replaceTagsWithRealData(res.result.reply, action.review),
                          } :
                          null;
                      return AiReplyActions.getAiAssistedReplySuccess({
                        aiAssistedReply: aiReply,
                        id: action.review?.id,
                      });
                    }),
                    catchError((err: HttpErrorResponse) => of(handleBeError({
                      userFriendlyMessage: this.translate.instant('ERROR'),
                      error: err,
                    })).pipe(
                        // for loading state handling in the local store service
                        mergeMap((a) => [a, AiReplyActions.getAiAssistedReplyError()]),
                    )),
                ),
        ),
    );
  });

  sendReplyForAnalysis$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(AiReplyActions.sendForAnalysis, reviewComponentDestroyed),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) => action.type === reviewComponentDestroyed.type ?
            EMPTY :
            this.apiService.replyAnalytics(accountId,
                action.reviewId,
                action.replyText,
                action.aiAssistedReplyText,
                action.gbpLocationId,
                action.aiAssistedRequestId)
                .pipe(
                    map(() => AiReplyActions.sendForAnalysisSuccess()),
                    catchError((err: HttpErrorResponse) => of(handleBeError({
                      userFriendlyMessage: this.translate.instant('ERROR'),
                      error: err,
                    }))),
                ),
        ),
    );
  });

  /**
   * Reset stored review translations and AI replies on accountID change (Security and Memory)
   */
  deleteTranslationsAndAiReplies$ = createEffect(() => {
    return this.store.select(selectAccountID).pipe(
        filter((accountId) => !!accountId),
        map(() => ReviewsActions.garbageCollectStaleData({})),
    );
  });

  /**
   * Smart Garbage Collection - Calculates and dispatches which translations/replies should be
   * evicted when the active reviews list successfully reloads, filters, or pages.
   */
  garbageCollectStaleData$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(ReviewsActions.getReviewsSuccess),
        concatLatestFrom(() => [
          this.store.select(selectReviewTranslations),
          this.store.select(selectAiAssistedReplies),
        ]),
        map(([action, translations, aiReplies]) => {
          const reviews = action.reviewsResponse.reviews;

          const translationsToRemove = translations
              .filter((t) => !reviews.find((review) => review.id === t.reviewId))
              .map((t) => t.id);

          const repliesToRemove = aiReplies
              .filter((r) => !reviews.find((review) => review.id === r.id))
              .map((r) => r.id);

          return ReviewsActions.garbageCollectStaleData({ translationsToRemove, repliesToRemove });
        }),
    );
  });

  apiCallError$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(handleBeError),
        tap((action) => {
          console.error(action.error);
          this.feedbackService.showAlertSnackbar({
            data: {
              userFriendlyMessage: action.userFriendlyMessage,
              detailedInfoFromBE: action.error?.error?.message ? [action.error.error.message] : null,
              actionButtons: action.actionButtons,
            }, isAddressListSnackbar: false,
          }, action.error?.status);
        }),
    );
  }, { dispatch: false });

  reloadDataOnAccountSwitch$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(updateSelectedAccount),
        concatLatestFrom(() => this.store.select(selectCurrentRoute)),
        // effect should run only if current route is /reviews
        filter(([_, currentRoute]) => currentRoute?.includes(MainRoutes.REVIEWS)),
        mergeMap(() => [
          // clear filters (which causes to refresh review list)
          ReviewSyncActions.syncReviewFilters({
            reviewFilters: {
              storeCodes: [],
              businessNames: [],
              countryCodes: [],
              cities: [],
              gbpAccountIds: [],
              from: startOfDay(subDays(new Date(), 1)).toISOString(),
              to: endOfDay(new Date()).toISOString(),
              sortType: SortType.NEWEST,
              reviewType: null,
              reply: null,
              edit: null,
              rating: [],
            },
          }),
          AiSettingsActions.getDefaultLanguages(),
          AiSettingsActions.getAiSettings(),
          ReplyTemplatesActions.getReplyTemplates(),
        ]),
    );
  });


  // HELPERS


  /**
   * Generates a file name for the exported reviews file. In general, the file name is the username. If a daterange is
   * selected, these dates will be added to the name. For the other filters, if at least one of them is applied
   * '_filtered' will be added to the filename.
   * @param params
   * @param filtered
   * @param accountName
   * @param format
   */
  private generateExportFileName(params: ReviewRequestParams,
                                 filtered: boolean,
                                 accountName: string,
                                 format: ReviewExportFormatChoice): string {
    let filename = accountName;
    const fromDate = parseISODateString(params?.from);
    const toDate = parseISODateString(params?.to);

    if (fromDate) {
      filename = `${filename}_from_${formatDate(fromDate)}`;
    }
    if (toDate) {
      filename = `${filename}_to_${formatDate(toDate)}`;
    }
    if (filtered) {
      filename = filename.concat('_filtered');
    }

    filename = filename.concat('.' + format);
    return filename;
  }


}
